import type { LibrarySketch } from './types';

export const audioReactiveSketches: LibrarySketch[] = [
  {
    id: 'audio-waveform-oscilloscope',
    name: 'Oscilloscope',
    description: 'Classic oscilloscope display showing real-time audio waveform with phosphor glow and adjustable gain.',
    category: 'audio-reactive',
    tags: ['waveform', 'oscilloscope', 'audio', 'retro', 'scope'],
    runtimeMode: 'p5',
    inputTypes: ['audio'],
    params: [
      { name: 'gain', type: 'float', min: 0.5, max: 5.0, default: 1.0, label: 'Gain' },
      { name: 'lineWeight', type: 'float', min: 1, max: 5, default: 2, label: 'Line Weight' },
      { name: 'glowColor', type: 'color', default: '#00ff88', label: 'Glow Color' },
      { name: 'persistence', type: 'int', min: 5, max: 60, default: 20, label: 'Persistence' },
    ],
    code: `// @param float gain 0.5 5.0 1.0
// @param float lineWeight 1 5 2
// @param color glowColor #00ff88
// @param int persistence 5 60 20

function setup() {
  createCanvas(genie.width, genie.height);
}
function draw() {
  const g = genie.params.gain ?? 1.0;
  const lw = genie.params.lineWeight ?? 2;
  const persist = genie.params.persistence ?? 20;
  background(0, persist);
  const waveform = genie.audio.waveform;
  if (!waveform || waveform.length === 0) {
    stroke(40); strokeWeight(1);
    line(0, height / 2, width, height / 2);
    noStroke(); fill(60); textAlign(CENTER);
    text('Connect audio input', width / 2, height / 2 - 20);
    return;
  }
  // Grid lines
  stroke(20); strokeWeight(0.5);
  for (let i = 0; i < 10; i++) {
    const x = (i / 10) * width;
    line(x, 0, x, height);
    const y = (i / 10) * height;
    line(0, y, width, y);
  }
  // Glow pass
  const col = color(genie.params.glowColor ?? '#00ff88');
  for (let pass = 0; pass < 3; pass++) {
    const alpha = pass === 0 ? 30 : pass === 1 ? 60 : 255;
    const weight = lw * (3 - pass);
    stroke(red(col), green(col), blue(col), alpha);
    strokeWeight(weight);
    noFill();
    beginShape();
    for (let i = 0; i < waveform.length; i++) {
      const x = map(i, 0, waveform.length, 0, width);
      const y = map(waveform[i] * g, -1, 1, height, 0);
      vertex(x, y);
    }
    endShape();
  }
}`,
  },

  {
    id: 'audio-frequency-bars',
    name: 'Frequency Bars',
    description: 'Classic frequency spectrum analyzer with smoothed bars, peak hold indicators, and gradient coloring.',
    category: 'audio-reactive',
    tags: ['frequency', 'spectrum', 'bars', 'audio', 'analyzer'],
    runtimeMode: 'p5',
    inputTypes: ['audio'],
    params: [
      { name: 'barCount', type: 'int', min: 16, max: 128, default: 64, label: 'Bars' },
      { name: 'smoothing', type: 'float', min: 0.5, max: 0.99, default: 0.85, label: 'Smoothing' },
      { name: 'mirror', type: 'bool', default: false, label: 'Mirror' },
      { name: 'barGap', type: 'float', min: 0, max: 4, default: 1, label: 'Gap' },
    ],
    code: `// @param int barCount 16 128 64
// @param float smoothing 0.5 0.99 0.85
// @param bool mirror false
// @param float barGap 0 4 1

let smoothed = [], peaks = [];
function setup() {
  createCanvas(genie.width, genie.height);
  colorMode(HSB, 360, 100, 100, 100);
  const n = genie.params.barCount ?? 64;
  smoothed = new Array(n).fill(0);
  peaks = new Array(n).fill(0);
}
function draw() {
  background(0);
  const fft = genie.audio.fft;
  const n = genie.params.barCount ?? 64;
  const sm = genie.params.smoothing ?? 0.85;
  const gap = genie.params.barGap ?? 1;
  const doMirror = genie.params.mirror ?? false;
  if (!fft || fft.length === 0) {
    fill(60); noStroke(); textAlign(CENTER);
    text('Connect audio input', width / 2, height / 2);
    return;
  }
  const barW = width / n - gap;
  for (let i = 0; i < n; i++) {
    const idx = floor(map(i, 0, n, 0, fft.length / 2));
    const val = (fft[idx] + 140) / 140;
    smoothed[i] = lerp(smoothed[i], val, 1 - sm);
    if (smoothed[i] > peaks[i]) peaks[i] = smoothed[i];
    else peaks[i] *= 0.995;
    const h = smoothed[i] * height;
    const hue = map(i, 0, n, 200, 0);
    noStroke();
    fill(hue, 80, 100, 90);
    const x = i * (barW + gap);
    if (doMirror) {
      rect(x, height / 2, barW, -h / 2);
      rect(x, height / 2, barW, h / 2);
    } else {
      rect(x, height - h, barW, h);
    }
    fill(0, 0, 100);
    const peakY = doMirror ? height / 2 - peaks[i] * height / 2 : height - peaks[i] * height;
    rect(x, peakY, barW, 2);
  }
}`,
  },

  {
    id: 'audio-reactive-rings',
    name: 'Bass Reactive Rings',
    description: 'Concentric rings that pulse and expand with bass frequencies, creating a hypnotic tunnel effect.',
    category: 'audio-reactive',
    tags: ['rings', 'bass', 'concentric', 'pulse', 'tunnel'],
    runtimeMode: 'p5',
    inputTypes: ['audio'],
    params: [
      { name: 'ringCount', type: 'int', min: 5, max: 30, default: 15, label: 'Rings' },
      { name: 'bassGain', type: 'float', min: 0.5, max: 5.0, default: 2.0, label: 'Bass Gain' },
      { name: 'rotationSpeed', type: 'float', min: 0.0, max: 0.05, default: 0.01, label: 'Rotation' },
      { name: 'hueOffset', type: 'float', min: 0, max: 360, default: 0, label: 'Hue Offset' },
    ],
    code: `// @param int ringCount 5 30 15
// @param float bassGain 0.5 5.0 2.0
// @param float rotationSpeed 0.0 0.05 0.01
// @param float hueOffset 0 360 0

function setup() {
  createCanvas(genie.width, genie.height);
  colorMode(HSB, 360, 100, 100, 100);
}
function draw() {
  background(0, 40);
  translate(width / 2, height / 2);
  const n = genie.params.ringCount ?? 15;
  const bg = genie.params.bassGain ?? 2.0;
  const rs = genie.params.rotationSpeed ?? 0.01;
  const ho = genie.params.hueOffset ?? 0;
  const fft = genie.audio.fft;
  const amp = genie.audio.amplitude ?? 0;
  const bass = fft && fft.length > 4 ? ((fft[0] + fft[1] + fft[2] + fft[3]) / 4 + 140) / 140 : 0.5;
  const bassNorm = bass * bg;
  rotate(frameCount * rs);
  noFill();
  strokeWeight(2);
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const baseR = map(i, 0, n, 20, min(width, height) * 0.45);
    const pulse = bassNorm * 20 * (1 - t * 0.5);
    const r = baseR + pulse + sin(frameCount * 0.03 + i * 0.5) * 5;
    const hue = (ho + t * 180 + frameCount * 0.5) % 360;
    stroke(hue, 80, 100, 80 - t * 50);
    const verts = floor(map(i, 0, n, 3, 60));
    beginShape();
    for (let j = 0; j <= verts; j++) {
      const a = (j / verts) * TWO_PI;
      const wobble = sin(a * 3 + frameCount * 0.05 + i) * bassNorm * 5;
      vertex(cos(a) * (r + wobble), sin(a) * (r + wobble));
    }
    endShape(CLOSE);
  }
}`,
  },

  {
    id: 'audio-terrain',
    name: 'Audio Terrain',
    description: 'A 3D terrain mesh driven by audio frequency data, creating mountains and valleys from sound.',
    category: 'audio-reactive',
    tags: ['terrain', '3d', 'audio', 'mesh', 'frequency'],
    runtimeMode: 'threejs',
    inputTypes: ['audio'],
    params: [
      { name: 'heightGain', type: 'float', min: 5, max: 80, default: 30, label: 'Height Gain' },
      { name: 'gridSize', type: 'int', min: 20, max: 80, default: 50, label: 'Grid Size' },
      { name: 'scrollSpeed', type: 'float', min: 0.1, max: 2.0, default: 0.5, label: 'Scroll Speed' },
      { name: 'wireColor', type: 'color', default: '#ff44aa', label: 'Wire Color' },
    ],
    code: `// @param float heightGain 5 80 30
// @param int gridSize 20 80 50
// @param float scrollSpeed 0.1 2.0 0.5
// @param color wireColor #ff44aa

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(60, genie.width / genie.height, 0.1, 500);
camera.position.set(0, 40, 60);
camera.lookAt(0, 0, 0);
const renderer = new THREE.WebGLRenderer({ canvas: genie.canvas, antialias: true });
renderer.setSize(genie.width, genie.height);
renderer.setClearColor(0x000000);
const gs = genie.params.gridSize ?? 50;
const geo = new THREE.PlaneGeometry(120, 120, gs, gs);
geo.rotateX(-Math.PI / 2);
const col = genie.params.wireColor ?? '#ff44aa';
const mat = new THREE.MeshBasicMaterial({ color: col, wireframe: true });
const mesh = new THREE.Mesh(geo, mat);
scene.add(mesh);
scene.fog = new THREE.Fog(0x000000, 40, 150);
let offset = 0;
function animate() {
  requestAnimationFrame(animate);
  const hg = genie.params.heightGain ?? 30;
  const ss = genie.params.scrollSpeed ?? 0.5;
  offset += ss * 0.02;
  const fft = genie.audio.fft;
  const pos = geo.attributes.position;
  const cols = gs + 1;
  for (let i = 0; i < pos.count; i++) {
    const ix = i % cols;
    const iz = Math.floor(i / cols);
    let h = 0;
    if (fft && fft.length > 0) {
      const fi = Math.floor((ix / cols) * (fft.length / 2));
      h = ((fft[fi] || 0) + 140) / 140 * hg;
    }
    const wave = Math.sin((iz * 0.3) + offset * 20) * 2;
    pos.setY(i, h + wave);
  }
  pos.needsUpdate = true;
  camera.position.x = Math.sin(offset * 2) * 20;
  camera.lookAt(0, 10, 0);
  renderer.render(scene, camera);
}
animate();`,
  },
];
